import { NFTContractScheme, type Auth } from 'oscar-typedef'
import { EthereumPrepareData } from '../prepareData/EthereumPrepareData'
import type { EthereumSendExtraData } from '../sendExtraData/EthereumSendExtraData'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
import type { ITransactionExecutor, EncUVD, Nft } from '@/types'

export class EthereumNftTransactionExecutor implements ITransactionExecutor {
  private nft: Nft
  private preparedTransaction: any = null // V2: prepareTransaction 응답의 transaction 객체 저장

  constructor(params: { nft: Nft }) {
    this.nft = params.nft
  }

  private get gasDecimal() {
    return (this.nft.account?.coin?.property?.gasDecimal as number) ?? 9
  }

  private getStringSchema(): string {
    // V2: nft.standard 사용 (ERC721, ERC1155)
    if (this.nft.standard) {
      return this.nft.standard
    }
    // V1 fallback: contract.schema 사용
    switch (this.nft.contract?.schema) {
      case NFTContractScheme.ERC721:
      case NFTContractScheme.KIP17:
        return 'ERC721'
      case NFTContractScheme.ERC1155:
      case NFTContractScheme.KIP37:
        return 'ERC1155'
      default:
        return 'ERC721'
    }
  }

  async getPrepareData(params: {
    to: string
    amount: string
  }): Promise<EthereumPrepareData> {
    // V2: prepare API 사용 (NFT 전송은 type: 'nft')
    const data = await WepinBackendAPI.getInstance().transaction.prepare({
      accountId: this.nft.accountId,
      type: 'nft',
      recipient: params.to,
      amount: params.amount,
      nft: {
        standard: this.getStringSchema(),
        contractAddress:
          this.nft.contractAddress || this.nft.contract?.address?.toString(),
        tokenId: this.nft.tokenId?.toString(),
      },
    })

    // V2: transaction 객체 저장 (sign에서 사용)
    this.preparedTransaction = data.transaction

    // V2 응답 구조: transaction.preparedFee에 가스 정보 있음
    const transaction = data.transaction
    const preparedFee = transaction?.preparedFee

    return new EthereumPrepareData({
      gasPrice: {
        high: preparedFee?.high ?? '0',
        medium: preparedFee?.medium ?? '0',
        low: preparedFee?.low ?? '0',
      },
      gasLimit: parseInt(preparedFee?.gasLimit ?? '0'),
      nonce: transaction?.nonce ?? 0,
      decimal: this.nft.coin?.decimals ?? 18,
      feeType: preparedFee?.type, // V2: 'fixed-fee' 등
    })
  }

  async sign(params: {
    to: string
    amount: string
    encUVD: EncUVD
    extraData: EthereumSendExtraData
    otpCode?: Auth.VerifyCode.Request.Body
  }): Promise<any> {
    if (!this.nft.account) {
      throw new Error('Account is not found')
    }
    if (!this.preparedTransaction) {
      await this.getPrepareData({ to: params.to, amount: params.amount })
    }
    if (!this.preparedTransaction) {
      throw new Error(
        'preparedTransaction is not exist. Call getPrepareData first.',
      )
    }

    // V2: signTransaction API 호출 (prepareTransaction 응답값 그대로 전달)
    const signParams: any = {
      accountId: this.nft.accountId,
      transaction: this.preparedTransaction,
      UVD: params.encUVD,
    }

    // OTP 코드가 있으면 추가
    if (params.otpCode) {
      signParams.otpCode = params.otpCode
    }

    const result =
      await WepinBackendAPI.getInstance().transaction.sign(signParams)

    return result
  }

  async broadcast(params: { signResponse: any }): Promise<any> {
    if (!this.nft.account) {
      throw new Error('Account is not found')
    }

    // V2: broadcastTransaction API 호출
    const result = await WepinBackendAPI.getInstance().transaction.broadcast({
      accountId: this.nft.accountId,
      signedOperation: params.signResponse,
    })

    return result
  }
}
